/**
 * `error_code` → operator-facing Arabic copy, for the codes **more than one
 * feature meets**. A code only one surface can raise belongs in that feature's
 * own `errors.ts`.
 *
 * Rules the entries follow (`errors-and-observability.md`, design system
 * §9.18): the copy states the next step rather than the failure, carries no
 * status numeral, no code, and no technical vocabulary. The code itself is a
 * log key and never reaches the interface; the `X-Request-ID` is what an
 * operator quotes.
 *
 * **Never inline a backend code in JSX.**
 */

export interface ErrorCopy {
  /** One sentence, in the operator's language, naming what to do next. */
  message: string;
  /**
   * `danger` for an action that failed destructively, `neutral` for an
   * upstream failure nobody in this console caused — a 502 styled as a danger
   * teaches an operator that the platform is angry at them.
   */
  tone: 'danger' | 'neutral' | 'warning';
}

export const errorCatalog: Readonly<Record<string, ErrorCopy>> = {
  // ---- session ----------------------------------------------------------
  AUTH_PHONE_INVALID: { message: 'رقم الهاتف يحتاج صيغة يمنية كاملة.', tone: 'warning' },
  AUTH_OTP_INVALID: { message: 'الرمز غير مطابق. اطلب رمزاً جديداً ثمّ أعد الإدخال.', tone: 'warning' },
  AUTH_OTP_EXPIRED: { message: 'انتهت صلاحية الرمز. اطلب رمزاً جديداً.', tone: 'warning' },
  // The owning service raises `AUTH_OTP_RESEND_BARRED` (`backend-auth`
  // §9.2 `POST /api/v1/otps`). An earlier spelling of this key here read
  // `…_TOO_SOON`, which no service ever emits — the copy would have been
  // unreachable and the operator would have met the bare 429 fallback.
  // NOT «the previous code is still valid». `backend-auth` REPLACES a live code
  // on every request — the newest digits win and the old ones die — so nothing
  // is waiting to expire. This 429 is the per-number SPEND cap
  // (`OTP_REQUEST_PER_IDENTIFIER_LIMIT` over `LOGIN_LOCKOUT_WINDOW_SECONDS`),
  // and telling the operator to wait for a code that no longer exists sent them
  // to watch a countdown that could not end their wait.
  AUTH_OTP_RESEND_BARRED: {
    message: 'طلبات كثيرة على هذا الرقم خلال وقت قصير. انتظر قليلاً ثمّ اطلب رمزاً جديداً.',
    tone: 'warning',
  },
  AUTH_RATE_LIMITED: { message: 'طلبات كثيرة في وقت قصير. انتظر قليلاً ثمّ أعد المحاولة.', tone: 'warning' },
  AUTH_METHOD_NOT_PERMITTED: {
    message: 'طريقة الدخول هذه غير مفعّلة. استخدم الطريقة المعروضة.',
    tone: 'warning',
  },
  AUTH_USER_BLOCKED: { message: 'هذا الحساب محظور. راجع مسؤولاً لرفع الحظر.', tone: 'danger' },
  AUTH_LOGIN_LOCKED: {
    message: 'تعذّر الدخول مرّات متتالية. انتظر قليلاً ثمّ أعد المحاولة.',
    tone: 'danger',
  },
  AUTH_SESSION_REVOKED: { message: 'انتهت الجلسة. سجّل الدخول من جديد.', tone: 'warning' },
  AUTH_REFRESH_REPLAYED: { message: 'انتهت الجلسة. سجّل الدخول من جديد.', tone: 'warning' },
  AUTH_ORIGIN_NOT_PERMITTED: {
    message: 'تعذّر إتمام الطلب من هذا العنوان. أعد تحميل الصفحة، وإن تكرّر فأبلغ الدعم.',
    tone: 'danger',
  },

  // ---- upstream ---------------------------------------------------------
  AUTH_SMS_DISPATCH_FAILED: { message: 'تعذّر إرسال الرمز الآن. أعد المحاولة بعد قليل.', tone: 'neutral' },
  AUTH_UPSTREAM_TIMEOUT: { message: 'الخدمة مشغولة الآن. أعد المحاولة بعد قليل.', tone: 'neutral' },
};

/** Fallbacks by status, for a code no catalog entry names. */
const STATUS_FALLBACK: Readonly<Record<number, ErrorCopy>> = {
  400: { message: 'تعذّر إتمام الطلب بالبيانات الحالية. راجع الحقول ثمّ أعد المحاولة.', tone: 'warning' },
  401: { message: 'انتهت الجلسة. سجّل الدخول من جديد.', tone: 'warning' },
  403: { message: 'هذا الإجراء خارج صلاحيّتك. راجع مسؤولاً.', tone: 'warning' },
  404: { message: 'لم نعثر على العنصر المطلوب. قد يكون حُذف أو نُقل.', tone: 'warning' },
  409: {
    message: 'تغيّرت حالة العنصر بينما كانت الصفحة مفتوحة. أعد التحميل ثمّ أعد المحاولة.',
    tone: 'warning',
  },
  422: { message: 'راجع الحقول المعلَّمة ثمّ أعد الإرسال.', tone: 'warning' },
  429: { message: 'طلبات كثيرة في وقت قصير. انتظر قليلاً ثمّ أعد المحاولة.', tone: 'warning' },
  500: { message: 'حدث خلل في الخدمة. أعد المحاولة، وإن تكرّر فأبلغ الدعم.', tone: 'danger' },
  502: { message: 'الخدمة غير متاحة الآن. أعد المحاولة بعد قليل.', tone: 'neutral' },
  504: { message: 'تأخّرت الخدمة عن الردّ. أعد تحميل الصفحة للاطّلاع على النتيجة.', tone: 'neutral' },
};

const UNKNOWN: ErrorCopy = {
  message: 'حدث خلل غير متوقّع. أعد المحاولة، وإن تكرّر فأبلغ الدعم.',
  tone: 'danger',
};

/**
 * Resolves copy for one failure. A feature passes its own map first, so a
 * surface-specific sentence wins over the shared one without either being
 * duplicated.
 */
export function resolveErrorCopy(
  code: string | undefined,
  status: number,
  featureCatalog?: Readonly<Record<string, ErrorCopy>>,
): ErrorCopy {
  if (code) {
    const local = featureCatalog?.[code];
    if (local) return local;

    const shared = errorCatalog[code];
    if (shared) return shared;
  }

  return STATUS_FALLBACK[status] ?? UNKNOWN;
}

/**
 * Whether a failure is the caller's **role**, rather than anything that went wrong.
 *
 * Read from the status first and the code's suffix second, because the nine
 * services namespace their codes per service — `ANALYTICS_FORBIDDEN`,
 * `SUPPORT_FORBIDDEN`, `CONTENT_FORBIDDEN` — and enumerating nine spellings here
 * would leave the tenth rendering as a generic failure. The suffix carries the
 * meaning; the prefix carries only which service said it.
 *
 * The distinction is not cosmetic. A refusal on a role attempted nothing, so
 * «nothing changed» is answering a question nobody asked, and a *Retry* button
 * on it cannot succeed however many times it is pressed.
 */
export function isPermissionRefusal(code: string | undefined, status: number): boolean {
  return status === 403 || (code?.endsWith('_FORBIDDEN') ?? false);
}
